import { useCallback } from 'react';
import type { ChatMessage } from '../_contexts/DocumentRagContext';
import { useDocumentRagContext } from '../_contexts/DocumentRagContext';

/**
 * Hook to access DocumentRagContext with convenience methods
 *
 * @example
 * ```typescript
 * const { state, addMessage, clearMessages, setSelectedDocument } = useDocumentRag();
 * ```
 */
export function useDocumentRag() {
  const { state, dispatch } = useDocumentRagContext();

  /**
   * Add a message to the chat history
   */
  const addMessage = useCallback(
    (message: ChatMessage) => {
      dispatch({ type: 'ADD_MESSAGE', payload: message });
    },
    [dispatch]
  );

  /**
   * Update an existing message at the specified index
   */
  const updateMessage = useCallback(
    (index: number, message: ChatMessage) => {
      dispatch({ type: 'UPDATE_MESSAGE', payload: { index, message } });
    },
    [dispatch]
  );

  /**
   * Set all messages in the chat history (replaces existing messages)
   */
  const setMessages = useCallback(
    (messages: ChatMessage[]) => {
      dispatch({ type: 'SET_MESSAGES', payload: messages });
    },
    [dispatch]
  );

  /**
   * Clear all messages from the chat history
   */
  const clearMessages = useCallback(() => {
    dispatch({ type: 'CLEAR_MESSAGES' });
  }, [dispatch]);

  /**
   * Set the selected document ID for preview
   */
  const setSelectedDocumentId = useCallback(
    (documentId: string | null) => {
      dispatch({ type: 'SET_SELECTED_DOCUMENT_ID', payload: documentId });
    },
    [dispatch]
  );

  /**
   * Set the conversation ID (typically called when API returns a new conversationId)
   */
  const setConversationId = useCallback(
    (conversationId: string | null) => {
      dispatch({ type: 'SET_CONVERSATION_ID', payload: conversationId });
    },
    [dispatch]
  );

  return {
    /** Current state */
    state,
    /** Dispatch function for custom actions */
    dispatch,
    /** Convenience methods */
    addMessage,
    updateMessage,
    setMessages,
    clearMessages,
    setSelectedDocumentId,
    setConversationId,
  };
}
